import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Formik } from 'formik';
import Fuse from 'fuse.js';
import * as Yup from 'yup';
import { Button, Checkbox, Dropdown, FieldControl, Icon, SingleSelect, DateTime } from '~/components';
import { Table, TableBoxRowActions } from '~/components/table';
import { mergeValues, dateFormats, capitalize } from '~/utils';

function HolidayForm({ holiday, holidays, selectedHolidays, setEditForm, onCancel, onSave }) {
  const formRef = useRef();
  const selectRef = useRef();
  const [filterValue, setFilterValue] = useState('');

  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.open();
    }
  }, []);

  const allHolidays = useMemo(() => {
    let data = _.cloneDeep(holidays);
    // Remove any holidays that have already been selected
    for (const item of selectedHolidays) {
      if (holiday && holiday.id == item.id) {
        continue;
      }
      data = _.reject(data, { id: item.id });
    }
    // If holiday does not exist in holidays, add it.
    // This might occur if the holiday is inactive, but already added to the schedule.
    if (holiday && !_.find(data, { id: holiday.id })) {
      data.push(holiday);
    }
    return _.sortBy(data, ['name']);
  }, [holiday, holidays, selectedHolidays]);

  const options = useMemo(() => {
    if (!filterValue) {
      return allHolidays;
    }

    const fuse = new Fuse(allHolidays, { keys: ['id', 'name'] });
    const results = fuse.search(filterValue);
    return results.map((result) => result.item);
  }, [allHolidays, filterValue]);

  const initialValues = mergeValues(
    {
      id: '',
      isStatutory: false,
    },
    holiday,
  );

  useEffect(() => {
    setEditForm(formRef);
    return () => {
      setEditForm(null);
    };
  }, [setEditForm]);

  const handleSave = (values) => {
    const selectedHoliday = _.find(allHolidays, { id: values.id });
    onSave({
      ...selectedHoliday,
      isStatutory: values.isStatutory,
    });
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSave}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        id: Yup.string().required('Select a holiday to continue.'),
        isStatutory: Yup.boolean(),
      })}>
      {({ errors, setFieldValue, submitForm, touched, values }) => {
        const selectedOption = _.find(allHolidays, { id: values.id });
        return (
          <Table.BoxRow focused onEnter={submitForm}>
            <Table.Cell style={{ flex: '1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%' }}>
                <FieldControl error={touched.id && errors.id} style={{ flex: 1 }}>
                  <SingleSelect
                    ref={selectRef}
                    showFilter
                    noOptionsMessage="No more holidays available"
                    onChange={(event) => setFieldValue('id', event.target.value)}
                    onFilterChange={(event) => setFilterValue(event.target.value)}
                    value={values.id}
                    valueRenderer={selectedOption?.name || ''}
                    style={{ width: '100%' }}>
                    {options.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </SingleSelect>
                </FieldControl>
              </div>
            </Table.Cell>

            <FieldControl style={{ padding: '0 1rem', fontSize: '14px' }}>
              <Checkbox
                label="Statutory"
                checked={values.isStatutory}
                onChange={(event) => setFieldValue('isStatutory', event.target.checked)}
              />
            </FieldControl>
            <TableBoxRowActions.Form onSubmit={submitForm} onCancel={onCancel} />
          </Table.BoxRow>
        );
      }}
    </Formik>
  );
}

function DaysFromNow({ date }) {
  const now = moment().format(dateFormats.isoDate);
  const then = moment(date);
  if (!date || !then.isValid()) {
    return '';
  }
  const diff = then.diff(now, 'days');
  if (Math.abs(diff) > 1) {
    return capitalize(then.from(now));
  }
  if (diff === -1) return 'Yesterday';
  if (diff === 1) return 'Tomorrow';
  return 'Today';
}

function HolidayScheduleHolidayRow({
  holiday,
  holidays,
  selectedHolidays,
  isEditing,
  setEditForm,
  onCancel,
  onDelete,
  onEdit,
  onSave,
  onToggleStatutory,
}) {
  const nextOccurrence = useMemo(() => {
    const today = moment().format(dateFormats.isoDate);
    const futureDates = _(holiday?.occurrences || [])
      .map((occurrence) => moment(occurrence.date).format(dateFormats.isoDate))
      .filter((date) => moment(date).isSameOrAfter(today, 'day'))
      .sort()
      .value();
    if (futureDates.length <= 0) {
      return null;
    }
    return futureDates[0];
  }, [holiday]);

  if (!isEditing && !holiday) {
    return (
      <Table.Row style={{ borderBottom: 'none' }}>
        <Table.Cell>
          <Button isAnchor isStrong onClick={onEdit}>
            <Icon icon="plus" size="xs" spaceRight />
            Quick Add
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  }
  if (!isEditing) {
    return (
      <Table.BoxRow>
        <Table.Cell>{holiday.name}</Table.Cell>
        <Table.Cell>
          {nextOccurrence ? (
            <div>
              <DaysFromNow date={nextOccurrence} />
              {' ('}
              <DateTime value={nextOccurrence} format={dateFormats.compactDate} />
              {')'}
            </div>
          ) : (
            '—'
          )}
        </Table.Cell>
        <Table.Cell>{holiday.isStatutory ? 'Yes' : 'No'}</Table.Cell>
        <TableBoxRowActions>
          <TableBoxRowActions.Dropdown>
            {({ setIsOpen }) => (
              <>
                <Dropdown.Item
                  onClick={() => {
                    onToggleStatutory(holiday);
                    setIsOpen(false);
                  }}>
                  {holiday.isStatutory ? 'Unset as Statutory' : 'Set as Statutory'}
                </Dropdown.Item>

                <Dropdown.DeleteItem onClick={() => onDelete(holiday)}>Delete</Dropdown.DeleteItem>
              </>
            )}
          </TableBoxRowActions.Dropdown>
        </TableBoxRowActions>
      </Table.BoxRow>
    );
  }
  return (
    <HolidayForm
      holiday={holiday}
      holidays={holidays}
      selectedHolidays={selectedHolidays}
      setEditForm={setEditForm}
      onCancel={onCancel}
      onSave={onSave}
    />
  );
}

export default HolidayScheduleHolidayRow;
